<template>
	<div class="date-picker-calendar">
		<div class="date-picker-calendar__header">
			<Button
				:id="`arrow_left${uid}`"
				:className="'btn--icon-only date-picker-calendar__previous-btn'"
				iconClassName="icon-arrow-left"
				:iconBeforeLabel="true"
				:btnClick="onClickPreviousMonth"
				v-if="showPreviousMonthButton"
			/>
			<Button
				:id="uid"
				className="btn--link"
				variant="text"
				:labelText="monthLabel"
				:btnClick="onMonthLabelClick"
			/>
			<Button
				:id="`arrow_right${uid}`"
				:className="'btn--icon-only date-picker-calendar__next-btn'"
				iconClassName="icon-arrow-right"
				:iconBeforeLabel="true"
				:btnClick="onClickNextMonth"
				v-if="showNextMonthButton"
			/>
		</div>
		<table class="date-picker-calendar__table">
			<tr class="date-picker-calendar__weekdays">
				<th
					class="date-picker-calendar__weekdays-day"
					v-for="day in narrowDayValues"
					:key="day.id"
				>
					{{ day.dayCode }}
				</th>
			</tr>
			<tr
				class="date-picker-calendar__weeks-of-month"
				v-for="week in weeksOfMonth"
				:key="week.id"
			>
				<td
					:class="getClassList(day)"
					v-for="day in week"
					:key="day.toDateString()"
					@click="onDayClicked(day)"
					tabindex="0"
				>
					<span class="date-picker-calendar__weeks-of-month-day-text">
						{{ day.getDate() }}
					</span>
				</td>
			</tr>
		</table>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import dateLocaleMixin from '@/mixins/date-locale-mixin';
import {
	addDays,
	compareAsc,
	compareDesc,
	getDay,
	getDaysInMonth,
	getMonth,
	getWeekOfMonth,
	isSameDay,
	lastDayOfWeek,
	isDate
} from 'date-fns';

export default {
	name: 'DatePickerCalendar',
	mixins: [dateLocaleMixin],
	data() {
		return {
			monthNumber: undefined,
			year: undefined,
			narrowDayValues: this.getDayObjectArray(),
			weeksOfMonth: undefined,
			numberOfDaysInMonth: undefined,
			monthLabel: undefined,
			notSameMonthClass: 'not-same-month',
			isSelectedClass: 'is-selected',
			isRangeDayClass: 'is-range-day',
			isTodayClass: 'is-today'
		};
	},
	components: {
		Button
	},
	props: {
		/**
		 * Single Date object or range object in format:
		 * {
		 * 	startDate: Date,
		 * 	endDate: Date.
		 * 	today: Date
		 * }
		 */
		value: {
			required: true
		},
		selectionType: {
			type: String,
			required: true
		},
		monthDate: {
			type: Date,
			required: true
		},
		showPreviousMonthButton: {
			type: Boolean,
			default: false
		},
		showNextMonthButton: {
			type: Boolean,
			default: false
		},
		/**
		 * values narrow, abbreviated, wide
		 */
		dayNameWidth: {
			type: String,
			default: 'narrow'
		},
		startDate: {
			type: [Date]
		},
		disableDaysBeforeStartDate: {
			type: Boolean,
			default: false
		},
		disableDaysAfterMaxDate: {
			type: Boolean,
			default: false
		},
		maxDate: {
			type: [Date]
		}
	},
	created() {
		this.monthNumber = this.monthDate.getMonth();
		this.year = this.monthDate.getFullYear();
		this.numberOfDaysInMonth = getDaysInMonth(this.monthDate);
		this.weeksOfMonth = this.getWeeksOfMonth();
		this.monthLabel = this.getMonthLabel();
	},
	computed: {},
	methods: {
		/**
		 * This function will return any programatic CSS class names for a day cell
		 * when:
		 * 		the date is not in the current month ('not-same-month')
		 * 		the date is selected ('is-selected')
		 * 		the date is between two selected dates ('is-range-day')
		 * 		the date is highlighted as today ('is-today')
		 */
		getClassList(day) {
			// Define the base class for all days
			const classList = ['date-picker-calendar__weeks-of-month-day'];

			// Determine if the day is not in the same month as the current month
			const dayNotInThisMonth = getMonth(day) !== this.monthNumber;

			// If the day is not in the same month as the current month or if we want disable before startDate, add the corresponding class
			if (
				dayNotInThisMonth ||
				(this.disableDaysBeforeStartDate && this.startDate > day)
			) {
				classList.push(this.notSameMonthClass);
			}

			// If the day is not in the same month as the current month or if we want disable after endDate, add the corresponding class
			if (
				dayNotInThisMonth ||
				(this.disableDaysAfterMaxDate && this.maxDate < day)
			) {
				classList.push(this.notSameMonthClass);
			}

			// If the day is selected, add the corresponding class
			if (
				!dayNotInThisMonth &&
				this.value &&
				(isSameDay(this.value, day) ||
					isSameDay(this.value.startDate, day) ||
					isSameDay(this.value.endDate, day))
			) {
				classList.push(this.isSelectedClass);
			}

			// If the day is part of a selected date range, add the corresponding class
			if (
				!dayNotInThisMonth &&
				this.value &&
				this.value.startDate &&
				isDate(this.value.startDate) &&
				compareAsc(this.value.startDate, day) === -1 &&
				this.value.endDate &&
				isDate(this.value.endDate) &&
				compareDesc(this.value.endDate, day) === -1
			) {
				classList.push(this.isRangeDayClass);
			}

			// If the day is today, add the corresponding class
			if (isSameDay(day, new Date())) {
				classList.push(this.isTodayClass);
			}

			// Return the list of classes
			return classList;
		},
		getWeeksOfMonth() {
			const weekArray = [];
			let dayArray = [];

			//go through all days in the month
			for (let index = 1; index <= this.numberOfDaysInMonth; index++) {
				const targetDate = new Date(this.year, this.monthNumber, index);
				const nextDate = addDays(targetDate, 1);
				const targetDayOfWeek = getDay(targetDate);

				//first day of the month may not be first day of week
				if (index === 1 && targetDayOfWeek > 0) {
					for (let index3 = 0; index3 < targetDayOfWeek; index3++) {
						const subtractindex = index3 - targetDayOfWeek;
						const previousMonthDay = addDays(
							targetDate,
							subtractindex
						);
						dayArray.push(previousMonthDay);
					}
				}

				dayArray.push(targetDate);

				//if it's the last day of the month,
				//see if we need to fill in the last week with days from next month
				if (index === this.numberOfDaysInMonth) {
					const lastDayDate = lastDayOfWeek(targetDate);
					const lastDay = getDay(lastDayDate);
					const targetDay = getDay(targetDate);

					if (lastDay > targetDay) {
						let nextMonthDayIndex = 1;

						for (
							let index2 = targetDay + 1;
							index2 <= lastDay;
							index2++
						) {
							const dayOfNextMonth = addDays(
								targetDate,
								nextMonthDayIndex
							);

							dayArray.push(dayOfNextMonth);
							nextMonthDayIndex += 1;
						}
					}
				}

				if (getWeekOfMonth(nextDate) !== getWeekOfMonth(targetDate)) {
					//weekArray is an array of array of days
					weekArray.push(dayArray);
					dayArray = [];
				}
			}

			return weekArray;
		},
		getDayObjectArray() {
			const dayValues = this.getDayValues(this.dayNameWidth); //function from locale mixin
			const dayObjectArray = [];

			for (let index = 0; index < dayValues.length; index++) {
				dayObjectArray.push({
					id: index,
					dayCode: dayValues[index]
				});
			}

			return dayObjectArray;
		},
		getMonthLabel() {
			return `${this.getMonthName()} ${this.year}`;
		},
		getMonthName() {
			return this.locale.localize.month(this.monthNumber, {
				width: 'wide'
			});
		},
		onClickPreviousMonth() {
			this.$emit('clickPreviousMonth');
		},
		onClickNextMonth() {
			this.$emit('clickNextMonth');
		},
		onDayClicked(day) {
			this.$emit('dayClicked', day);
		},
		onMonthLabelClick() {
			this.$emit('monthLabelClick');
		}
	}
};
</script>

<style lang="scss" scoped>
.date-picker-calendar {
	min-width: 246px;

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		position: relative;

		margin: $gutter-xl 0;

		> * {
			font-size: $default-font-size + 2;
			min-height: 40px;
		}
	}

	&__table {
		width: 100%;
		border-spacing: 0;
	}

	&__weekdays-day {
		padding: $padding-sm;
	}

	&__weekdays-day,
	&__weeks-of-month-day {
		font-size: 13px;
		@include mq($until: md) {
			width: 42px;
			height: 42px;
		}
		width: 32px;
		height: 32px;
	}

	&__weeks-of-month-day {
		text-align: center;
		cursor: pointer;
		user-select: none;
		border: $focus-width $focus-style transparent;
		border-color: transparent;
		border-radius: $border-radius;

		&.is-selected {
			background: $btn-primary-bkgd;
			color: $white;
		}

		&:hover {
			background: $btn-primary-bkgd-hover;
			color: $white;
		}

		&:focus,
		&:focus-visible {
			border-color: $btn-primary-bkgd;
			outline-color: $btn-primary-bkgd;
		}

		&.is-today {
			position: relative;

			&::after {
				@include vertical-horizontal-center-with-transform;
				content: '';
				width: 32px;
				height: 32px;
				border-radius: 50%;
				border: $focus-width $focus-style $black;
			}

			&.is-selected {
				border-radius: $border-radius;
				background: $btn-primary-bkgd;
				color: $white;
				border-color: $btn-primary-bkgd;
			}
		}

		&.is-range-day {
			background: $ui-positive-light;
			border-radius: 0;

			&:hover {
				background: $btn-primary-bkgd-hover;
				color: $white;
				border-radius: $border-radius;
			}

			&.is-today {
				border: none;

				&::after {
					content: '';
					@include vertical-horizontal-center-with-transform;
					width: 32px;
					height: 32px;
					border-radius: 50%;
					border: $focus-width $focus-style $black;
				}
			}
		}
	}

	&__weeks-of-month-day.not-same-month {
		.date-picker-calendar__weeks-of-month-day-text {
			opacity: 0.3;
		}
	}

	&__weeks-of-month-day-text {
		width: 100%;
		height: 100%;
	}
}

.not-same-month {
	pointer-events: none;

	&.is-today {
		&::after {
			display: none;
		}
	}
}
</style>
